export interface InvoiceItem {
    rowNumber: string;
    destination: string;
    date: string;
    bolNumber: string;
    orderNumber: string;
    loadNumber: string;
    invoiceNumber: string;
    terminal: string;
    gross: string;
    net: string;
    rate: string;
    fuelSurchargePercent: string;
    terminalArrivalTime: string;
    terminalDepartTime: string;
    deliveryArrivalTime: string;
    deliveryDepartTime: string;
    driver: string;
    notes: string;
    totalDem: string;
    pumpEnabled: boolean;
    pumpCount: string;
    invoiceDate: string;
    dueDate: string;
    miles: string;
    customer: string;
    address: string;
    cityStateZip: string;
    freightRev: number;
    fuelSurcharge: string;
    demurrageCharge: number;
    totalCharges: number;
    broker: string;
    invoiceSheet: string;
    // Split loads: `isSplit` marks the generated SPLIT entry; `splitId` links the
    // SPLIT entry and its member loads so they can be unselected together;
    // `splitShareAmount` is each member's share of the split total, shown as a
    // "Split" line item on that member's invoice.
    isSplit?: boolean;
    splitId?: string;
    splitShareAmount?: number;
}

export const defaultInvoiceItem: InvoiceItem = {
    rowNumber: '4',
    destination: '',
    date: '',
    bolNumber: '',
    orderNumber: '',
    loadNumber: '',
    invoiceNumber: '',
    terminal: '',
    gross: '',
    net: '',
    rate: '',
    fuelSurchargePercent: '',
    terminalArrivalTime: '',
    terminalDepartTime: '',
    deliveryArrivalTime: '',
    deliveryDepartTime: '',
    driver: '',
    notes: '',
    totalDem: '',
    pumpEnabled: false,
    pumpCount: '1',
    invoiceDate: '',
    dueDate: '',
    miles: '',
    customer: '',
    address: '',
    cityStateZip: '',
    freightRev: 0,
    fuelSurcharge: '',
    demurrageCharge: 0,
    totalCharges: 0,
    broker: '',
    invoiceSheet: 'Freight Warriors'
};

const DELIVERY_STATUSES = {
    PENDING: 'Pending',
    IN_TRANSIT: 'In Transit',
    DELIVERED: 'Delivered',
    DELAYED: 'Delayed',
    CANCELLED: 'Cancelled',
} as const;

const TERMINALS = [
    'Chevron',
    'Kinder Morgan',
    'Tesoro'
] as const;

const DRIVERS = [
    'Alex',
    'Haitham',
    'Mauricio'
] as const;

export interface LoadFormData {
    broker: string;
    destination: string;
    orderNumber: string;
    loadNumber: string;
    deliveryDate: string;
    gallons: string;
    pumpRequired: boolean;
    status: typeof DELIVERY_STATUSES[keyof typeof DELIVERY_STATUSES];
    terminal: string;
    driver: string;
    currentDate: string;
    pdfFile: File | null;
    pdfFileName: string;
}

export {
    DELIVERY_STATUSES,
    TERMINALS,
    DRIVERS
};

export interface DeductionItem {
  name: string;
  amount: number;
  description?: string;
}

export interface MonthlyDeductionRecord {
  month: string; // Format: 'YYYY-MM'
  deductions: DeductionItem[];
  processed: boolean;
  processedAt?: Date;
}

export interface DriverPayment {
  id: string;
  driverId: string;
  invoiceIds: string[];
  grossAmount: number;
  deductions: {
    percentageCut: number;
    monthlyDeductions: DeductionItem[];
    totalDeductions: number;
  };
  netAmount: number;
  status: 'pending' | 'processed' | 'paid';
  createdAt: Date;
  processedAt?: Date;
  paidAt?: Date;
  notes?: string;
}

export interface PaymentSummary {
  driverId: string;
  month: string; // Format: 'YYYY-MM'
  totalGrossAmount: number;
  totalDeductions: number;
  totalNetAmount: number;
  invoiceCount: number;
  lastPaymentDate?: Date;
}

export interface User {
  uid: string;
  email: string;
  displayName: string;
  accountType: 'admin' | 'owner' | 'driver';
  dogtag: string;
}

export type UserData = {
  id?: string;
  uid?: string;
  email?: string;
  name?: string;
  displayName?: string;
  accountType?: 'admin' | 'owner' | 'driver';
  dogtag?: string;
  [key: string]: any;
};

export interface FirebaseUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  dogtag: string;
  admin: boolean;
  driver: boolean;
  accountType?: 'admin' | 'owner' | 'driver';
  percentageCut: number;
  monthlyDeductions: DeductionItem[];
  createdAt?: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  driver: string;
  status: 'pending' | 'paid_to_fw' | 'paid_to_driver';
  totalCharges: number;
  createdAt: Date;
  updatedAt: Date;
  [key: string]: any;
}